/**
 * Deterministic column-width measurement for EditableTable.
 *
 * Walks every row in the dataset and measures cell text with a shared
 * Canvas 2D context — never samples, never reads DOM.  Sparse columns
 * with rare wide values are handled correctly because every row is
 * visited (canvas.measureText is microseconds, so visiting 5k rows
 * across 10 columns finishes in single-digit milliseconds).
 *
 * Columns whose cells include non-text decoration (icons, badges, etc.)
 * can opt into a second DOM-confirmation pass via the `measureDOM` flag.
 * That pass is exposed as a separate async function so callers can run
 * it in `requestIdleCallback` after first paint.
 *
 * Result widths are clamped to [minWidth, maxWidth] when declared on
 * the column.  Columns with an explicit numeric `width` skip measurement
 * entirely and are returned unchanged.  String widths (e.g. '10em') are
 * not included in the numeric result map — the caller resolves those
 * directly from the column definition at render time.
 */

export interface MeasureColumn<T> {
    label: string;
    /** Explicit width.  Numeric values skip measurement; string values are not measured. */
    width?: string | number;
    /** Lower clamp for measured width. */
    minWidth?: number;
    /** Upper clamp for measured width.  Cells with longer content wrap or truncate at render time. */
    maxWidth?: number;
    /** Optional text source for columns whose cell content is computed (e.g. `${first} ${last}`). */
    measureTextFn?: (item: T) => string;
    /** Opt into a DOM-confirmation pass — for columns with significant non-text decoration. */
    measureDOM?: boolean;
}

export interface MeasureContext {
    /** CSS font shorthand resolved from a live cell, e.g. '14px Inter, sans-serif'. */
    font: string;
    /** Horizontal padding (left + right) added to measured text width. */
    paddingX: number;
    /** Reserve for fixed-position icons or other decoration the canvas can't see. */
    iconReserveX?: number;
}

export interface MeasureResult {
    /** Per-column pixel width, keyed by `MeasureColumn.label`. */
    widths: Record<string, number>;
    /** Epoch ms of the measurement that produced this result. */
    measuredAt: number;
}

let sharedCanvas: HTMLCanvasElement | null = null;
let sharedCtx: CanvasRenderingContext2D | null = null;

function getCanvasContext(font: string): CanvasRenderingContext2D {
    if (!sharedCanvas) {
        sharedCanvas = document.createElement('canvas');
        sharedCtx = sharedCanvas.getContext('2d');
    }
    if (!sharedCtx) throw new Error('Canvas 2D context unavailable');
    sharedCtx.font = font;
    return sharedCtx;
}

function getTextFor<T>(item: T, col: MeasureColumn<T>): string {
    if (col.measureTextFn) {
        try {
            const r = col.measureTextFn(item);
            return r == null ? '' : String(r);
        } catch {
            return '';
        }
    }
    const v = (item as Record<string, unknown>)[col.label];
    if (v == null) return '';
    if (typeof v === 'object') return '';
    return String(v);
}

function clamp(measured: number, col: MeasureColumn<unknown>): number {
    let w = measured;
    if (col.minWidth != null) w = Math.max(w, col.minWidth);
    if (col.maxWidth != null) w = Math.min(w, col.maxWidth);
    return Math.max(0, w);
}

/**
 * Measure every column across every row using a shared canvas.
 * Synchronous; ~5ms for 5k rows × 10 columns.
 */
export function measureColumnWidths<T>(
    items: readonly T[],
    columns: readonly MeasureColumn<T>[],
    ctx: MeasureContext,
): MeasureResult {
    const c2d = getCanvasContext(ctx.font);
    const widths: Record<string, number> = {};
    const reserve = ctx.iconReserveX ?? 0;

    for (const col of columns) {
        if (typeof col.width === 'number') {
            widths[col.label] = col.width;
            continue;
        }
        if (typeof col.width === 'string') {
            // Caller handles string widths directly; we don't include them.
            continue;
        }

        let maxText = 0;
        for (const item of items) {
            const text = getTextFor(item, col);
            if (!text) continue;
            const w = c2d.measureText(text).width;
            if (w > maxText) maxText = w;
        }

        const measured = Math.ceil(maxText + ctx.paddingX + reserve);
        widths[col.label] = clamp(measured, col);
    }

    return {widths, measuredAt: Date.now()};
}

/**
 * Measure only newly-arrived rows and grow widths only where needed.
 * Used for incremental data arrivals (filter results, live sync, append).
 * Returns the merged result; the previous `measuredAt` is preserved when
 * no column grew.
 */
export function measureNewRows<T>(
    newItems: readonly T[],
    columns: readonly MeasureColumn<T>[],
    ctx: MeasureContext,
    previous: MeasureResult,
): MeasureResult {
    const fresh = measureColumnWidths(newItems, columns, ctx);
    const widths: Record<string, number> = {...previous.widths};
    let changed = false;

    for (const col of columns) {
        const prev = previous.widths[col.label] ?? 0;
        const next = fresh.widths[col.label] ?? 0;
        if (next > prev) {
            widths[col.label] = clamp(next, col);
            changed = true;
        }
    }

    return {
        widths,
        measuredAt: changed ? Date.now() : previous.measuredAt,
    };
}

const CACHE_PREFIX = 'EditableTable:widths:';
// 2: widths cached under version 1 were floored at the stretched cell box and can pin a
// column far wider than its content; contentExtent measures what the content paints.
const CACHE_VERSION = 2;

interface CacheEnvelope {
    v: number;
    widths: Record<string, number>;
    measuredAt: number;
}

export function getCachedWidths(key: string): MeasureResult | null {
    try {
        const raw = sessionStorage.getItem(CACHE_PREFIX + key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as CacheEnvelope;
        if (parsed?.v !== CACHE_VERSION) return null;
        if (!parsed.widths || typeof parsed.widths !== 'object') return null;
        return {widths: parsed.widths, measuredAt: parsed.measuredAt};
    } catch {
        return null;
    }
}

export function setCachedWidths(key: string, result: MeasureResult): void {
    try {
        const envelope: CacheEnvelope = {
            v: CACHE_VERSION,
            widths: result.widths,
            measuredAt: result.measuredAt,
        };
        sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(envelope));
    } catch {
        // sessionStorage may be unavailable (private mode, quota) — fail silently.
    }
}

export function clearCachedWidths(key: string): void {
    try {
        sessionStorage.removeItem(CACHE_PREFIX + key);
    } catch {
        // ignore
    }
}

export function buildCacheKey(propsName: string, columnKey: string, dataVersion: string): string {
    return `${propsName}|${columnKey}|${dataVersion}`;
}
